import React, {
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { AuthError, loginFormType } from "../types/form-login-types";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { GoogleLoginButton } from "./google-login-button";
import { Link, useLocation } from "react-router-dom";
import { useMediaQuery } from "@/hooks/use-media-query";
import { isDesktopSize } from "utils/is-desktop";
import { useForm } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { EyeClosedIcon } from "@radix-ui/react-icons";
import { Eye, RefreshCwIcon } from "lucide-react";
import {
  endPointConfirmAccount,
  endPointCreateAccount,
  endPointPasswordRestore,
  endPointResendAccountCode,
} from "modules/main/services/endpoints";
import { toast } from "sonner";
import { AuthContext } from "../context/auth-provider";
import { cn } from "@/lib/utils";
import { MainLogo } from "modules/main/assets/main_logo";
import { AnimatePresence, motion } from "framer-motion";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  hasSelectedLocation,
  readUserDataFromLocalStorage,
} from "modules/main/services/location-storage";
import { getVisitorId } from "utils/analytics";
import { CreateAccountForm } from "./create-account-form";
import { useCreateAccount, useCreateAccountSchema } from "./use-create-account";

export const RECAPTCHA_SITE_KEY = "6LfdoEgrAAAAALg4NMmdACxKsI9BKCNPEkoi1rP0";
// Esquema para el formulario de login
const loginFormSchema = z.object({
  username: z.string().email({ message: "Debe ingresar un email válido" }),
  password: z.string().min(1, { message: "Debes ingresar una contraseña" }),
});

// Esquema para el formulario de recuperación de contraseña
const passwordRestoreSchema = z.object({
  username: z.string().email({ message: "Debe ingresar un email válido" }),
});

/**
 * Re-export of the registration form schema, kept under the historical
 * `createAccountSchema` name for backwards compatibility with external
 * callers that may have imported it from this file before the
 * onboarding step-6 extraction.
 *
 * The source of truth lives in `use-create-account.tsx`. The
 * `LoginRegisterDialog` consumes it through `CreateAccountForm`, which
 * binds its own `react-hook-form` instance to the exported schema.
 */
export const createAccountSchema = useCreateAccountSchema;

interface LoginDialogProps extends React.HTMLAttributes<HTMLDivElement> {
  // loginAuth: (values: loginFormType, from:string) => Promise<void>;
  buttonName?: string;
  description?: string;
  variant?: "default" | "outline";
  size?: "sm" | "lg";
  mobile?: boolean;
  buttonIcon?: React.ComponentType<{ className?: string }>;
  headLess?: boolean;
  setOpen?: (open: boolean) => void;
  open?: boolean;
  loginRegisterSwitch?: "login" | "register";
}

export const LoginDialog = forwardRef<HTMLDivElement, LoginDialogProps>(
  (props, ref) => {
    const { loginAuth, loginAuthGoogle } = useContext(AuthContext);
    const [open, setOpen] = useState(false);

    const IconComponent = props.buttonIcon;

    return (
      <Dialog onOpenChange={props.setOpen} open={props.open}>
        {!props.headLess && (
          <DialogTrigger asChild className={cn("text-white", props.className)}>
            {props.children ? (
              props.children
            ) : (
              <span>
                <Button
                  variant={props.variant ?? "default"}
                  data-ph-event="open-login-dialog"
                  className={cn("font-semibold text-whi", props.className, {
                    "text-xxs": props.mobile,
                    "text-foreground": props.variant === "outline",
                  })}
                  size={props.size ?? "sm"}
                >
                  {IconComponent && <IconComponent className="w-4 h-4 mr-2" />}
                  {props.buttonName ?? "Ingresar"}
                </Button>
              </span>
            )}
          </DialogTrigger>
        )}

        <DialogContent className="text-xs py-8">
          <LoginForm loginAuth={loginAuth} description={props.description} />
        </DialogContent>
      </Dialog>
    );
  },
);

export const LoginRegisterDialog: React.FC<LoginDialogProps> = ({
  ...props
}) => {
  const { loginAuth, loginAuthGoogle } = useContext(AuthContext);
  const [loginRegisterSwitch, setLoginRegisterSwitch] = useState<
    "login" | "register"
  >(props.loginRegisterSwitch ?? "login");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (props.loginRegisterSwitch) {
      setLoginRegisterSwitch(props.loginRegisterSwitch);
    }
  }, [props.loginRegisterSwitch]);

  const currentOpen = props.open ?? open;
  const handleOpenChange = props.setOpen ?? setOpen;

  // Hook owned by the dialog so the extracted CreateAccountForm can
  // mount as a plain presentational child. The dialog closes itself
  // when the OTP confirm resolves with a valid session (the hook's
  // onAuthenticated callback fires AFTER checkAuth resolves).
  const createAccountHook = useCreateAccount({
    onAuthenticated: () => handleOpenChange(false),
    onBackToRegister: () => setLoginRegisterSwitch("login"),
  });

  return (
    <Dialog onOpenChange={handleOpenChange} open={currentOpen}>
      <DialogContent>
        <motion.div
          layout
          className="overflow-hidden"
          transition={{ layout: { duration: 0.28, ease: [0.22, 1, 0.36, 1] } }}
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={loginRegisterSwitch}
              layout
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{
                duration: 0.22,
                ease: "easeOut",
                layout: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
              }}
            >
              {loginRegisterSwitch === "login" ? (
                <LoginForm
                  loginAuth={loginAuth}
                  description={props.description}
                  onSwitchToRegister={() => setLoginRegisterSwitch("register")}
                />
              ) : (
                <DialogHeader>
                  <MainLogo classname="w-20 mx-auto my-4" />
                  <DialogTitle className="text-xl font-bold text-center">
                    ¡Tu ahorro te espera!
                  </DialogTitle>
                  <DialogDescription className="text-left text-sm text-muted-foreground w-4/5 mx-auto">
                    <p className="text-center">
                      {createAccountHook.confirmMessage
                        ? `Te hemos enviado un código de activación a ${
                            createAccountHook.activationEmail || "tu correo"
                          } para terminar de crear tu cuenta.`
                        : "Crea tu cuenta y accede al detalle de tus carritos optimizados, guardar tus listas y recibir notificaciones."}
                    </p>
                  </DialogDescription>
                </DialogHeader>
              )}
              {loginRegisterSwitch === "register" && (
                <CreateAccountForm
                  hook={createAccountHook}
                  showLogo={false}
                  showGoogleButton
                  showSwitchToLogin
                  onSwitchToLogin={() => setLoginRegisterSwitch("login")}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};

interface LoginFormProps extends React.HTMLAttributes<HTMLDivElement> {
  message?: string;
  description?: string;
  loginAuth: (values: loginFormType, from: string) => Promise<void>;
  onSwitchToRegister?: () => void;
}

export const LoginForm = forwardRef<HTMLDivElement, LoginFormProps>(
  ({ className, ...props }, ref) => {
    const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
    const [createAccountOpen, setCreateAccountOpen] = useState(false);
    const [reCaptchaIsReady, setReCaptchaIsReady] = useState(
      Boolean(window.grecaptcha?.ready),
    );

    useEffect(() => {
      const setReady = () => {
        window.grecaptcha.ready(() => {
          setReCaptchaIsReady(true);
        });
      };

      if (typeof window.grecaptcha !== "undefined") {
        setReady();
        return;
      }

      const scriptSelector = `script[src=\"https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}\"]`;
      const existingScript = document.querySelector(scriptSelector);

      if (!existingScript) {
        const script = document.createElement("script");
        script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
        script.async = true;
        document.body.appendChild(script);
      }

      const checkRecaptcha = setInterval(() => {
        if (typeof window.grecaptcha !== "undefined") {
          setReady();
          clearInterval(checkRecaptcha);
        }
      }, 100);

      return () => {
        clearInterval(checkRecaptcha);
      };
    }, []);

    const dialogOption = () => {
      const createAccountHandler = (open: boolean) => {
        if (props.onSwitchToRegister) {
          props.onSwitchToRegister();
          return;
        }

        setCreateAccountOpen(open);
      };

      if (forgotPasswordOpen)
        return (
          <ForgotPasswordDialog
            open={forgotPasswordOpen}
            onOpenChange={setForgotPasswordOpen}
          />
        );
      if (createAccountOpen)
        return (
          <CreateAccountDialog
            open={createAccountOpen}
            onOpenChange={setCreateAccountOpen}
          />
        );
      return (
        <LoginComponent
          loginAuth={props.loginAuth}
          forgotPasswordHandler={setForgotPasswordOpen}
          createAccountHandler={createAccountHandler}
          message={props.message}
          description={props.description}
        />
      );
    };
    return (
      <div
        ref={ref}
        className={cn("flex flex-col items-center w-full", className)}
      >
        {reCaptchaIsReady && dialogOption()}
      </div>
    );
  },
);

interface LoginComponentProps extends React.HTMLAttributes<HTMLDivElement> {
  loginAuth: (values: loginFormType, from: string) => Promise<void>;
  forgotPasswordHandler: (arg0: boolean) => void;
  createAccountHandler: (arg0: boolean) => void;
  message?: string;
  description?: string;
}

const LoginComponent: React.FC<LoginComponentProps> = ({
  forgotPasswordHandler,
  createAccountHandler,
  loginAuth,
  ...props
}) => {
  const location = useLocation();
  const focusRef = useRef<HTMLDivElement>(null);

  const isDesktop = useMediaQuery(isDesktopSize);

  const [loginResponse, setLoginResponse] = useState<string>("");

  const [pwdVisible, setPwdVisible] = useState(false);

  const form = useForm<z.infer<typeof loginFormSchema>>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  useEffect(() => {
    if (!isDesktop) {
      focusRef.current?.focus();
    } else {
      form.setFocus("username");
    }
  }, [isDesktop, form]);

  const onSubmitHandler = async (values: z.infer<typeof loginFormSchema>) => {
    try {
      const from = (location.state as { from: string })?.from || "/store";
      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, {
        action: "login",
      });
      const data = {
        ...values,
        recaptchaToken: token,
      };
      await loginAuth(data, from);
    } catch (error) {
      console.log(error);
      if (error instanceof AuthError) {
        setLoginResponse(error.message);
      } else {
        console.log("An unexpected error occurred");
        setLoginResponse("Ha ocurrido un error, intenta más tarde por favor");
      }
    }
  };

  return (
    <>
      <div className="flex flex-col gap-2 text-center">
        <MainLogo classname="w-20 mx-auto my-4" />
        <h2 className="text-xl font-bold text-center">
          ¡Que bueno que estás de vuelta!
        </h2>
        <div className="text-left text-sm text-muted-foreground w-4/5 mx-auto">
          <p className="text-center">Ingresa para continuar ahorrando.</p>
        </div>
      </div>
      <div className="mx-auto w-full lg:w-[350px] gap-3 pt-2 pb-6">
        <div className="flex flex-col gap-6 pt-2">
          <GoogleLoginButton text="Ingresa con Google" />
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t"></span>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground font-bold">
                O ingresa con tu email
              </span>
            </div>
          </div>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmitHandler)}>
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <div className="grid gap-2">
                      {/* <FormLabel htmlFor="username">Email</FormLabel> */}
                      <FormControl>
                        <Input
                          id="username"
                          type="email"
                          placeholder="Email"
                          autoComplete="username"
                          required
                          {...field}
                        />
                      </FormControl>
                    </div>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <div className="grid gap-4 mt-4">
                      <div className="grid gap-2">
                        <FormControl>
                          <div className="flex items-center">
                            <Input
                              id="password"
                              type={pwdVisible ? "text" : "password"}
                              autoComplete="current-password"
                              required
                              {...field}
                              placeholder="Contraseña"
                              className="pr-10"
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              type="button"
                              className="-ml-8"
                              tabIndex={-1}
                              onClick={(e) => {
                                e.preventDefault();
                                setPwdVisible(!pwdVisible);
                              }}
                            >
                              {pwdVisible ? (
                                <EyeClosedIcon className="w-4 h-4 text-muted-foreground" />
                              ) : (
                                <Eye className="w-4 h-4 text-muted-foreground" />
                              )}
                            </Button>
                          </div>
                        </FormControl>
                        <div className="flex items-center">
                          {/* <FormLabel htmlFor="password">Contraseña</FormLabel> */}
                          <div
                            onClick={() => forgotPasswordHandler(true)}
                            className="ml-auto inline-block text-xs text-muted-foreground font-semibold underline cursor-pointer"
                          >
                            ¿Olvidaste tu contraseña?
                          </div>
                        </div>
                        <FormMessage>{loginResponse}</FormMessage>
                      </div>
                      <div className="flex flex-col items-center justify-center w-full gap-2 ">
                        <div className="flex flex-row justify-between w-2/3 text-xxs opacity-50 px-2 pt-4">
                          <Link
                            to="/terms-of-service"
                            className="text-muted-foreground"
                          >
                            Términos del Servicio
                          </Link>
                          <Link
                            to="/privacy-policy"
                            className="text-muted-foreground "
                          >
                            Política de Privacidad
                          </Link>
                        </div>
                        <Button
                          data-ph-event="create-user-form"
                          type="submit"
                          size={"lg"}
                          className="w-2/3 "
                        >
                          Ingresar
                        </Button>
                      </div>
                    </div>
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </div>
        {/* <div className="relative">
                    <div className="absolute inset-0 flex items-center"><span className="w-full border-t"></span></div>
                    <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">
                            O ingresa con
                        </span>
                    </div>                                   
                </div>
                <GoogleLoginButton />
                <div className="mt-4 text-center text-sm">
                    <span className="mr-2">No tienes cuenta?</span>
                    <div onClick={() => createAccountHandler(true)}
                        className="ml-auto inline-block text-sm underline cursor-pointer" >
                        ¡Crea una!
                    </div>
                </div>
                <div>
                    Al Ingresar aceptas los <Link to="/terms-of-service" className="text-primary underline">Términos del Servicio</Link> y la <Link to="/privacy-policy" className="text-primary underline">Política de Privacidad</Link>
                </div> */}
      </div>
    </>
  );
};

interface ForgotPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ForgotPasswordDialog: React.FC<ForgotPasswordDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const form = useForm<z.infer<typeof passwordRestoreSchema>>({
    resolver: zodResolver(passwordRestoreSchema),
    defaultValues: {
      username: "",
    },
  });
  // const {toast} = useToast();
  const [confirmMessage, setConfirmMessage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = async (values: z.infer<typeof passwordRestoreSchema>) => {
    try {
      setIsLoading(true);
      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, {
        action: "passwordReset",
      });
      const data = {
        ...values,
        recaptchaToken: token,
      };
      // Llamada al endpoint para restaurar contraseña usando el endpoint definido
      const response = await fetch(endPointPasswordRestore, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
        credentials: "include",
      });
      setConfirmMessage(true);
      form.reset();
    } catch (error) {
      console.error(error);
      toast.error("Ha ocurrido un problema. Por favor intenta más tarde.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-auto grid w-full lg:w-[350px] gap-6">
      <div className="grid gap-2 text-center">
        <h1 className="text-2xl font-bold">Recupera tu contraseña</h1>
        <p className="text-balance text-muted-foreground text-sm">
          {confirmMessage
            ? "Te hemos enviado un correo con instrucciones para recuperar tu contraseña."
            : "Ingresa tu email y te enviaremos un enlace para restablecer tu contraseña."}
        </p>
      </div>
      {!confirmMessage ? (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="m@ejemplo.com"
                      autoComplete="email"
                      required
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Procesando..." : "Recuperar"}
              </Button>
            </div>
          </form>
        </Form>
      ) : (
        <Button
          type="button"
          onClick={() => onOpenChange(false)}
          className="w-full"
        >
          Volver al Login
        </Button>
      )}
    </div>
  );
};

interface CreateAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSwitchToLogin?: () => void;
}

/**
 * @deprecated `CreateAccountDialog` is the legacy 565-line monolithic
 * registration component kept around ONLY because `LoginForm` consumes
 * it through its `createAccountOpen` state (out of scope for the
 * onboarding step-6 change). All new code MUST mount
 * `<CreateAccountForm hook={useCreateAccount({...})} />` instead, which
 * is composed by `LoginRegisterDialog` today and by the onboarding
 * Step 6 final screen for the `onboarding-step-six-auth` spec.
 *
 * A future task will refactor `LoginForm` to consume `CreateAccountForm`
 * and delete this legacy component. Until then, this file remains the
 * sole source of the legacy implementation to avoid duplicating the
 * `LoginForm` consume path.
 */
const CreateAccountDialog: React.FC<CreateAccountDialogProps> = ({
  open,
  onOpenChange,
  onSwitchToLogin,
}) => {
  const { checkAuth } = useContext(AuthContext);
  const form = useForm<z.infer<typeof createAccountSchema>>({
    resolver: zodResolver(createAccountSchema),
    defaultValues: {
      username: "",
      firstName: "",
      password: "",
    },
  });
  const [confirmMessage, setConfirmMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [activationEmail, setActivationEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isResendLoading, setIsResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpCode, setOtpCode] = useState("");
  const [otpIsInvalid, setOtpIsInvalid] = useState(false);
  const [pwdVisible, setPwdVisible] = useState(false);

  const [reCaptchaIsReady, setReCaptchaIsReady] = useState(
    Boolean(window.grecaptcha?.ready),
  );

  useEffect(() => {
    const setReady = () => {
      window.grecaptcha.ready(() => {
        setReCaptchaIsReady(true);
      });
    };

    if (typeof window.grecaptcha !== "undefined") {
      setReady();
      return;
    }

    const scriptSelector = `script[src=\"https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}\"]`;
    const existingScript = document.querySelector(scriptSelector);

    if (!existingScript) {
      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
      script.async = true;
      document.body.appendChild(script);
    }

    const checkRecaptcha = setInterval(() => {
      if (typeof window.grecaptcha !== "undefined") {
        setReady();
        clearInterval(checkRecaptcha);
      }
    }, 100);

    return () => {
      clearInterval(checkRecaptcha);
    };
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) {
      return;
    }

    const timer = window.setInterval(() => {
      setResendCooldown((previousValue) => {
        if (previousValue <= 1) {
          window.clearInterval(timer);
          return 0;
        }

        return previousValue - 1;
      });
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [resendCooldown]);

  const onSubmit = async (values: z.infer<typeof createAccountSchema>) => {
    try {
      setIsLoading(true);

      // Llamada al endpoint para crear usuario
      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, {
        action: "createAccount",
      });
      const currentUserData = readUserDataFromLocalStorage();
      const shouldSendLocation = hasSelectedLocation(currentUserData);
      const data = {
        ...values,
        recaptchaToken: token,
        ...(shouldSendLocation
          ? { location_id: currentUserData.location_id }
          : {}),
      };

      const response = await fetch(endPointCreateAccount, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-visitor-id": getVisitorId(),
        },
        body: JSON.stringify(data),
        credentials: "include",
      });
      const responseData = (await response.json()) as {
        status: boolean;
        detail: string;
      };
      if (response.status === 200 || response.status === 201) {
        setActivationEmail(values.username);
        setConfirmMessage(true);
        setErrorMessage("");
        setOtpCode("");
        setOtpIsInvalid(false);
        setResendCooldown(60);
        form.reset();
      }
      setErrorMessage(responseData.detail);
    } catch (error) {
      console.error(error);
      toast.error("Ha ocurrido un problema. Por favor intenta más tarde.");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Requests a new OTP code for account activation.
   */
  const onResendCode = async () => {
    if (!activationEmail || resendCooldown > 0 || isResendLoading) {
      return;
    }

    try {
      setIsResendLoading(true);
      const recaptchaToken = await window.grecaptcha.execute(
        RECAPTCHA_SITE_KEY,
        { action: "resendAccountCode" },
      );
      const response = await fetch(endPointResendAccountCode, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-visitor-id": getVisitorId(),
        },
        body: JSON.stringify({
          username: activationEmail,
          recaptchaToken,
        }),
        credentials: "include",
      });

      const responseData = (await response.json()) as {
        status?: boolean;
        message?: string;
        detail?: string;
      };

      if (response.status === 200) {
        setResendCooldown(60);
        toast.success(
          "Si la cuenta existe, enviaremos un nuevo código de confirmación.",
        );
        return;
      }

      if (response.status === 429) {
        const detailMessage =
          responseData.detail ??
          "Debes esperar 60 segundos para solicitar un nuevo código";
        const secondsMatch = detailMessage.match(/(\d+)/);
        const secondsLeft = secondsMatch ? Number(secondsMatch[1]) : 60;

        setResendCooldown(secondsLeft);
        return;
      }
      toast.error(
        responseData.detail ??
          "No fue posible reenviar el código de confirmación. Intenta nuevamente.",
      );
    } catch (error) {
      toast.error("Ha ocurrido un problema. Por favor intenta más tarde.");
    } finally {
      setIsResendLoading(false);
    }
  };

  const onBackToRegister = () => {
    setConfirmMessage(false);
    setErrorMessage("");
    setOtpCode("");
    setOtpIsInvalid(false);
    setResendCooldown(0);
  };

  /**
   * Maps backend confirmation errors to user-friendly messages and UI state.
   */
  const mapConfirmAccountError = (statusCode: number, detail?: string) => {
    const normalizedDetail = (detail ?? "").toLowerCase();
    const secondsMatch = (detail ?? "").match(/(\d+)/);
    const waitSeconds = secondsMatch ? Number(secondsMatch[1]) : undefined;

    if (statusCode === 429) {
      return {
        message:
          detail ??
          "Demasiados intentos. Espera unos segundos para volver a intentar.",
        isOtpInvalid: false,
        waitSeconds,
      };
    }

    if (statusCode === 400 && normalizedDetail.includes("humano")) {
      return {
        message: "No pudimos validar reCAPTCHA. Intenta nuevamente.",
        isOtpInvalid: false,
      };
    }

    if (
      normalizedDetail.includes("expir") ||
      normalizedDetail.includes("venc")
    ) {
      return {
        message: "El código expiró. Solicita uno nuevo para continuar.",
        isOtpInvalid: true,
      };
    }

    if (
      normalizedDetail.includes("invál") ||
      normalizedDetail.includes("inval") ||
      normalizedDetail.includes("incorrect")
    ) {
      return {
        message:
          "El código ingresado no es válido. Revisa e intenta nuevamente.",
        isOtpInvalid: true,
      };
    }

    return {
      message:
        detail ?? "No fue posible confirmar tu cuenta. Intenta nuevamente.",
      isOtpInvalid: statusCode === 400 || statusCode === 401,
    };
  };

  /**
   * Confirms the account OTP against backend and restores authenticated session.
   */
  const onVerifyAccount = async () => {
    if (!activationEmail || otpCode.length !== 6) {
      setOtpIsInvalid(true);
      return;
    }

    try {
      setIsLoading(true);
      const recaptchaToken = await window.grecaptcha.execute(
        RECAPTCHA_SITE_KEY,
        { action: "confirmAccount" },
      );
      const response = await fetch(endPointConfirmAccount, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-visitor-id": getVisitorId(),
        },
        body: JSON.stringify({
          username: activationEmail,
          code: otpCode,
          token: otpCode,
          recaptchaToken,
        }),
        credentials: "include",
      });

      const responseData = (await response.json()) as {
        status?: boolean;
        message?: string;
        detail?: string;
        user_data?: string;
      };

      if (response.ok && responseData.status) {
        setOtpIsInvalid(false);
        setErrorMessage("");
        await checkAuth(responseData.user_data);
        toast.info(
          responseData.message ?? "Tu cuenta ha sido confirmada con éxito.",
        );
        onOpenChange(false);
        return;
      }

      const mappedError = mapConfirmAccountError(
        response.status,
        responseData.detail ?? responseData.message,
      );
      if (mappedError.waitSeconds && mappedError.waitSeconds > 0) {
        setResendCooldown(mappedError.waitSeconds);
      }
      setOtpIsInvalid(mappedError.isOtpInvalid);
      setErrorMessage(mappedError.message);
    } catch (error) {
      toast.error("Ha ocurrido un problema. Por favor intenta más tarde.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <MainLogo classname="w-20 mx-auto my-4" />
        <DialogTitle className="text-xl font-bold text-center">
          ¡Tu ahorro te espera!
        </DialogTitle>
        <DialogDescription className="text-left text-sm text-muted-foreground w-4/5 mx-auto">
          <p className="text-center">
            {confirmMessage
              ? `Te hemos enviado un código de activación a ${activationEmail || "tu correo"} para terminar de crear tu cuenta.`
              : "Crea tu cuenta y accede al detalle de tus carritos optimizados, guardar tus listas y recibir notificaciones."}
          </p>
        </DialogDescription>
      </DialogHeader>
      <div className="mx-auto grid w-full lg:w-[350px] gap-3 pt-2 pb-6">
        {confirmMessage && (
          <div className="flex flex-col w-full">
            <div className="flex justify-center">
              <Field className="mx-auto">
                <div className="flex items-center justify-between">
                  <FieldLabel htmlFor="otp-verification">
                    Código de activación
                  </FieldLabel>
                  <Button
                    variant="outline"
                    size="xs"
                    type="button"
                    onClick={onResendCode}
                    disabled={
                      isResendLoading || resendCooldown > 0 || !activationEmail
                    }
                  >
                    <RefreshCwIcon />
                    {isResendLoading
                      ? "Reenviando..."
                      : resendCooldown > 0
                        ? `Re-enviar en ${resendCooldown}s`
                        : "Re-enviar código"}
                  </Button>
                </div>
                <InputOTP
                  maxLength={6}
                  pattern={REGEXP_ONLY_DIGITS}
                  value={otpCode}
                  onChange={(value) => {
                    setOtpCode(value);
                    if (errorMessage !== "") {
                      setErrorMessage("");
                    }
                    if (otpIsInvalid && value.length === 6) {
                      setOtpIsInvalid(false);
                    }
                  }}
                  aria-invalid={otpIsInvalid}
                  className="justify-center"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup>
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup>
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
                {otpIsInvalid && (
                  <p className="text-destructive text-xs mt-2 text-center">
                    Ingresa un código válido de 6 dígitos.
                  </p>
                )}
              </Field>
            </div>
            <button
              type="button"
              onClick={onBackToRegister}
              className="flex justify-center items-center mt-4 underline text-muted-foreground"
            >
              No es mi correo electronico
            </button>
          </div>
        )}
        {errorMessage !== "" && (
          <p className="text-destructive text-sm text-center">{errorMessage}</p>
        )}
        {!confirmMessage ? (
          <div className="flex flex-col gap-6 pt-2">
            <GoogleLoginButton text="Regístrate con Google" />
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t"></span>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground font-bold">
                  O regístrate con tu email
                </span>
              </div>
            </div>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
              >
                {/* <FieldGroup className="grid max-w-sm grid-cols-2"> */}
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      {/* <FormLabel>Nombre</FormLabel> */}
                      <FormControl>
                        <Input placeholder="Nombre" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="username"
                  render={({ field }) => (
                    <FormItem>
                      {/* <FormLabel>Email</FormLabel> */}
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="Email"
                          autoComplete="email"
                          required
                          {...field}
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        Esto se usará como tu nombre de usuario.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      {/* <FormLabel>Contraseña</FormLabel> */}
                      <FormControl>
                        {/* <Input type="password" placeholder="Contraseña" {...field} autoComplete="new-password" /> */}
                        <InputGroup>
                          <InputGroupInput
                            type={pwdVisible ? "text" : "password"}
                            placeholder="Contraseña"
                            {...field}
                            autoComplete="new-password"
                          />
                          <InputGroupAddon align="inline-end">
                            <Button
                              variant="ghost"
                              size="icon"
                              type="button"
                              className="-ml-8"
                              tabIndex={-1}
                              onClick={(e) => {
                                e.preventDefault();
                                setPwdVisible(!pwdVisible);
                              }}
                            >
                              {pwdVisible ? (
                                <EyeClosedIcon className="w-4 h-4 text-muted-foreground" />
                              ) : (
                                <Eye className="w-4 h-4 text-muted-foreground" />
                              )}
                            </Button>
                          </InputGroupAddon>
                        </InputGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex flex-col items-center justify-center w-full gap-2 ">
                  <div className="flex flex-row justify-between w-2/3 text-xxs opacity-50 px-2 pt-4">
                    <Link
                      to="/terms-of-service"
                      className="text-muted-foreground"
                    >
                      Términos del Servicio
                    </Link>
                    <Link
                      to="/privacy-policy"
                      className="text-muted-foreground "
                    >
                      Política de Privacidad
                    </Link>
                  </div>
                  <Button
                    data-ph-event="create-user-form"
                    type="submit"
                    disabled={isLoading}
                    size={"lg"}
                    className="w-2/3 "
                  >
                    {isLoading ? "Creando..." : "Crea tu cuenta"}
                  </Button>
                </div>
              </form>
            </Form>
            <div className="mt-4 text-center text-sm">
              <span className="mr-2 text-muted-foreground">
                Ya tienes cuenta?
              </span>
              <div
                onClick={() =>
                  onSwitchToLogin ? onSwitchToLogin() : onOpenChange(false)
                }
                className="ml-auto inline-block text-sm underline cursor-pointer"
              >
                ¡Inicia sesión!
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center w-full gap-2 mt-4">
            <Button
              data-ph-event="create-user-form"
              type="button"
              disabled={isLoading}
              onClick={onVerifyAccount}
              size={"lg"}
              className="w-2/3 "
            >
              {isLoading ? "Verificando..." : "Verificar cuenta"}
            </Button>
          </div>
        )}
      </div>
    </>
  );
};
